import { z } from "zod"
import { http } from "../lib/http"

const audited = {
	createdAt: z.string(),
	updatedAt: z.string(),
}

export const VerticalSchema = z.object({
	id: z.string(),
	name: z.string(),
	defaultHsCode: z.string().nullable(),
	defaultPackingSpec: z.string().nullable(),
	countryIds: z.array(z.string()),
	...audited,
})

export const CarrierSchema = z.object({
	id: z.string(),
	name: z.string(),
	scacCode: z.string().nullable(),
	...audited,
})

export const PortSchema = z.object({
	id: z.string(),
	unlocode: z.string(),
	name: z.string(),
	countryCode: z.string(),
	...audited,
})

export const LaneSchema = z.object({
	id: z.string(),
	name: z.string(),
	originUnlocode: z.string().nullable(),
	destinationUnlocode: z.string().nullable(),
	...audited,
})

export const WarehouseSchema = z.object({
	id: z.string(),
	name: z.string(),
	countryCode: z.string().nullable(),
	address: z.string().nullable(),
	...audited,
})

export const DocumentTypeSchema = z.object({
	id: z.string(),
	code: z.string(),
	name: z.string(),
	// Nullable 0..365 default — the doc-create form reads this to seed
	// `dueAt` on type-change. Source of truth is the engine API
	// (PRD §4 backfill in migration 0008); the helm no longer carries
	// a hardcoded fallback map.
	defaultDueDays: z.number().nullable(),
	...audited,
})

export const CarrierRegistryEntrySchema = z.object({
	scac: z.string(),
	displayName: z.string(),
	mode: z.enum(["manual", "api"]),
	enabled: z.boolean(),
	isConfigured: z.boolean(),
	registered: z.boolean(),
})

export type Vertical = z.infer<typeof VerticalSchema>
export type Carrier = z.infer<typeof CarrierSchema>
export type Port = z.infer<typeof PortSchema>
export type Lane = z.infer<typeof LaneSchema>
export type Warehouse = z.infer<typeof WarehouseSchema>
export type DocumentType = z.infer<typeof DocumentTypeSchema>
export type CarrierRegistryEntry = z.infer<typeof CarrierRegistryEntrySchema>

const makeListFetcher =
	<T extends z.ZodTypeAny>(path: string, schema: T) =>
	async (signal?: AbortSignal): Promise<z.infer<T>[]> => {
		const raw = await http<unknown>(path, signal !== undefined ? { signal } : {})
		return z.array(schema).parse(raw)
	}

export const listVerticals = makeListFetcher("/settings/verticals", VerticalSchema)
export const listCarriers = makeListFetcher("/settings/carriers", CarrierSchema)
export const listPorts = makeListFetcher("/settings/ports", PortSchema)
export const listLanes = makeListFetcher("/settings/lanes", LaneSchema)
export const listWarehouses = makeListFetcher("/settings/warehouses", WarehouseSchema)
export const listDocumentTypes = makeListFetcher("/settings/document-types", DocumentTypeSchema)
export const listCarrierRegistryState = makeListFetcher(
	"/settings/carriers/registry-state",
	CarrierRegistryEntrySchema,
)

/** POST /settings/verticals */
export interface CreateVerticalInput {
	readonly name: string
	readonly countryIds: string[]
	readonly defaultHsCode?: string | null
	readonly defaultPackingSpec?: string | null
}

export const createVertical = async (
	input: CreateVerticalInput,
	signal?: AbortSignal,
): Promise<Vertical> => {
	const raw = await http<unknown>("/settings/verticals", {
		method: "POST",
		json: input,
		...(signal !== undefined ? { signal } : {}),
	})
	return VerticalSchema.parse(raw)
}

/** PATCH /settings/verticals/:id/countries — re-assign a vertical's countries */
export const setVerticalCountries = async (
	verticalId: string,
	countryIds: string[],
): Promise<void> => {
	await http<unknown>(`/settings/verticals/${verticalId}/countries`, {
		method: "PATCH",
		json: { countryIds },
	})
}

/** POST /settings/document-types */
export interface CreateDocumentTypeInput {
	readonly code: string
	readonly name: string
}

export const createDocumentType = async (
	input: CreateDocumentTypeInput,
	signal?: AbortSignal,
): Promise<DocumentType> => {
	const raw = await http<unknown>("/settings/document-types", {
		method: "POST",
		json: input,
		...(signal !== undefined ? { signal } : {}),
	})
	return DocumentTypeSchema.parse(raw)
}

/** PATCH /settings/document-types/:id — currently only updates defaultDueDays */
export interface PatchDocumentTypeInput {
	readonly defaultDueDays: number | null
}

export const patchDocumentType = async (
	id: string,
	input: PatchDocumentTypeInput,
	signal?: AbortSignal,
): Promise<DocumentType> => {
	const raw = await http<unknown>(`/settings/document-types/${id}`, {
		method: "PATCH",
		json: input,
		...(signal !== undefined ? { signal } : {}),
	})
	return DocumentTypeSchema.parse(raw)
}

/** POST /settings/carriers */
export interface CreateCarrierInput {
	readonly name: string
	readonly scacCode?: string | null
}

export const createCarrier = async (
	input: CreateCarrierInput,
	signal?: AbortSignal,
): Promise<Carrier> => {
	const raw = await http<unknown>("/settings/carriers", {
		method: "POST",
		json: input,
		...(signal !== undefined ? { signal } : {}),
	})
	return CarrierSchema.parse(raw)
}

/** POST /settings/ports */
export interface CreatePortInput {
	readonly unlocode: string
	readonly name: string
	readonly countryCode: string
}

export const createPort = async (input: CreatePortInput, signal?: AbortSignal): Promise<Port> => {
	const raw = await http<unknown>("/settings/ports", {
		method: "POST",
		json: input,
		...(signal !== undefined ? { signal } : {}),
	})
	return PortSchema.parse(raw)
}

/** POST /settings/lanes */
export interface CreateLaneInput {
	readonly name: string
	readonly originUnlocode?: string | null
	readonly destinationUnlocode?: string | null
}

export const createLane = async (input: CreateLaneInput, signal?: AbortSignal): Promise<Lane> => {
	const raw = await http<unknown>("/settings/lanes", {
		method: "POST",
		json: input,
		...(signal !== undefined ? { signal } : {}),
	})
	return LaneSchema.parse(raw)
}

/** POST /settings/warehouses */
export interface CreateWarehouseInput {
	readonly name: string
	readonly countryCode?: string | null
	readonly address?: string | null
}

export const createWarehouse = async (
	input: CreateWarehouseInput,
	signal?: AbortSignal,
): Promise<Warehouse> => {
	const raw = await http<unknown>("/settings/warehouses", {
		method: "POST",
		json: input,
		...(signal !== undefined ? { signal } : {}),
	})
	return WarehouseSchema.parse(raw)
}

export const registriesQueryKeys = {
	all: ["registries"] as const,
	verticals: () => [...registriesQueryKeys.all, "verticals"] as const,
	carriers: () => [...registriesQueryKeys.all, "carriers"] as const,
	ports: () => [...registriesQueryKeys.all, "ports"] as const,
	lanes: () => [...registriesQueryKeys.all, "lanes"] as const,
	warehouses: () => [...registriesQueryKeys.all, "warehouses"] as const,
	documentTypes: () => [...registriesQueryKeys.all, "documentTypes"] as const,
	carriersRegistryState: () => [...registriesQueryKeys.all, "carriersRegistryState"] as const,
}
